import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormSwitch, FormTextField } from 'src/components/Forms';
import {
  planControlFormSx,
  planToggleLabelSx,
  planToggleSx,
  SectionAccordion,
  SectionAccordionDetails,
  SectionAccordionSummary,
  SectionSummaryContent,
  SectionSummaryTitle,
  SectionWrapper,
} from 'src/pages/Settings/memberships/components/plans-tab/styled';
import {
  PlansTabField,
  type PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';
import { EXTERNAL_URL_PATTERN, normalizeExternalUrl } from 'src/utils/url';

const MAX_CHECKOUT_TEXT_LENGTH = 280;
const MAX_URL_LENGTH = 2048;

type CtaSupportSectionProps = {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  readOnly: boolean;
};

export const CtaSupportSection = ({
  expanded,
  onExpandedChange,
  readOnly,
}: CtaSupportSectionProps) => {
  const { clearErrors, control, setValue } = useFormContext<PlansTabFormValues>();

  const [
    showContactSupportRaw,
    contactSupportLinkRaw,
    requireTosBeforePlansRaw,
    termsOfServiceUrlRaw,
    privacyPolicyUrlRaw,
  ] =
    (useWatch({
      control,
      name: [
        PlansTabField.OptionsShowContactSupport,
        PlansTabField.OptionsContactSupportLink,
        PlansTabField.OptionsRequireTosBeforePlans,
        PlansTabField.OptionsTermsOfServiceUrl,
        PlansTabField.OptionsPrivacyPolicyUrl,
      ],
    }) as [
      boolean | undefined,
      string | undefined,
      boolean | undefined,
      string | undefined,
      string | undefined,
    ]) ?? [];
  const showContactSupport = showContactSupportRaw ?? false;
  const contactSupportLink = contactSupportLinkRaw ?? '';
  const requireTosBeforePlans = requireTosBeforePlansRaw ?? false;
  const termsOfServiceUrl = termsOfServiceUrlRaw ?? '';
  const privacyPolicyUrl = privacyPolicyUrlRaw ?? '';

  return (
    <SectionWrapper data-testid="cta-support-section">
      <SectionAccordion
        elevation={0}
        expanded={expanded}
        TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
        onChange={(_, expandedValue) => {
          onExpandedChange(expandedValue);
        }}
        disableGutters
      >
        <SectionAccordionSummary expandIcon={<ChevronRightIcon />}>
          <SectionSummaryContent>
            <SectionSummaryTitle variant="h6">CTA &amp; support</SectionSummaryTitle>
          </SectionSummaryContent>
        </SectionAccordionSummary>

        <SectionAccordionDetails>
          {expanded ? (
            <Stack gap={2} pt={1}>
              <FormTextField
                type="textfield"
                name={PlansTabField.OptionsSetupCheckoutText}
                label="Setup checkout text"
                showErrorText
                disabled={readOnly}
                textFieldProps={{
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  multiline: true,
                  minRows: 2,
                  inputProps: { maxLength: MAX_CHECKOUT_TEXT_LENGTH },
                }}
              />

              <FormSwitch
                type="switch"
                name={PlansTabField.OptionsShowContactSupport}
                label={<Typography sx={planToggleSx}>Show contact support</Typography>}
                labelPlacement="start"
                sx={planControlFormSx}
                labelSx={planToggleLabelSx}
                switchFieldProps={{
                  size: 'small',
                  inputProps: { 'aria-label': 'Show contact support toggle' },
                }}
                disabled={readOnly}
                onChange={(event) => {
                  if (!(event.target as HTMLInputElement).checked) {
                    clearErrors(PlansTabField.OptionsContactSupportLink);
                  }
                }}
              />

              <FormTextField
                type="textfield"
                name={PlansTabField.OptionsContactSupportLink}
                label="Contact support link"
                required={showContactSupport ? 'Contact support link is required' : false}
                pattern={showContactSupport ? EXTERNAL_URL_PATTERN : undefined}
                showErrorText
                disabled={readOnly || !showContactSupport}
                textFieldProps={{
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  inputProps: { maxLength: MAX_URL_LENGTH },
                  placeholder: 'https://support.example.com',
                  onBlur: () => {
                    const normalized = normalizeExternalUrl(contactSupportLink);
                    if (!normalized) {
                      return;
                    }

                    setValue(PlansTabField.OptionsContactSupportLink, normalized, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  },
                }}
              />

              <FormSwitch
                type="switch"
                name={PlansTabField.OptionsRequireTosBeforePlans}
                label={
                  <Typography sx={planToggleSx}>
                    Require Terms of Service acceptance before plans
                  </Typography>
                }
                labelPlacement="start"
                sx={planControlFormSx}
                labelSx={planToggleLabelSx}
                switchFieldProps={{
                  size: 'small',
                  inputProps: { 'aria-label': 'Require Terms of Service acceptance toggle' },
                }}
                disabled={readOnly}
              />

              <FormTextField
                type="textfield"
                name={PlansTabField.OptionsTermsOfServiceUrl}
                label="Terms of Service URL"
                required={requireTosBeforePlans ? 'Terms of Service URL is required' : false}
                pattern={termsOfServiceUrl ? EXTERNAL_URL_PATTERN : undefined}
                showErrorText
                disabled={readOnly}
                textFieldProps={{
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  inputProps: { maxLength: MAX_URL_LENGTH },
                  placeholder: 'https://example.com/terms',
                  onBlur: () => {
                    const normalized = normalizeExternalUrl(termsOfServiceUrl);
                    if (!normalized) {
                      return;
                    }

                    setValue(PlansTabField.OptionsTermsOfServiceUrl, normalized, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  },
                }}
              />

              <FormTextField
                type="textfield"
                name={PlansTabField.OptionsPrivacyPolicyUrl}
                label="Privacy Policy URL"
                pattern={privacyPolicyUrl ? EXTERNAL_URL_PATTERN : undefined}
                showErrorText
                disabled={readOnly}
                textFieldProps={{
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  inputProps: { maxLength: MAX_URL_LENGTH },
                  placeholder: 'https://example.com/privacy',
                  onBlur: () => {
                    const normalized = normalizeExternalUrl(privacyPolicyUrl);
                    if (!normalized) {
                      return;
                    }

                    setValue(PlansTabField.OptionsPrivacyPolicyUrl, normalized, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  },
                }}
              />
            </Stack>
          ) : null}
        </SectionAccordionDetails>
      </SectionAccordion>
    </SectionWrapper>
  );
};
